import React from 'react';

// Minimal line-icon set (Feather-style paths), drawn with currentColor so a
// single `color` prop drives both the stroke and the ink-circle border.
const ICON_PATHS = {
  edit: <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />,
  delete: (
    <>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </>
  ),
  close: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  swap: (
    <>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </>
  ),
  note: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </>
  ),
  // Same counter-clockwise-arrow glyph as the "Previously..." tab bar icon
  // (TabBar.jsx's `history`), per direct user request to keep "view this
  // exercise's history" visually consistent with the tab that also shows
  // history -- was a plain "i" before (and before that, an "i" inside its
  // own circle, doubling up with IconButton's own ring); the icon KEY stays
  // `info` since every call site already names it that, only the glyph has
  // changed twice since -- first to match the "Previously..." tab bar
  // icon's counter-clockwise arrow, then (this pass) to an hourglass, per
  // direct user request to use an hourglass for every history-related icon
  // app-wide, matching TabBar.jsx's `history` glyph below.
  info: (
    <polyline points="6 4 18 4 12 12 18 20 6 20 12 12 6 4" />
  ),
  // Folded trifold map with a dashed-looking fold line down each seam --
  // used only by the "Take the Tour" button next to the Welcome heading
  // (App.js). Reads as "here's a guide to find your way around" far more
  // directly than the plain question-mark this replaced; renamed from
  // `help` to `map` along with the glyph swap since it still has exactly
  // the one call site, so there was nothing to preserve a legacy key for.
  map: (
    <>
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
      <line x1="8" y1="2" x2="8" y2="18" />
      <line x1="16" y1="6" x2="16" y2="22" />
    </>
  )
};

/**
 * Small circular tappable icon button -- replaces the plain underlined
 * text links (Edit/Delete/Remove/cancel) this app used to have for the same
 * actions. `color` carries whatever semantic color the text link it
 * replaces used to have (orange for destructive/edit, dark for a neutral
 * cancel, green for the one "edit a past logged set" exception), applied to
 * both the icon and its border via currentColor. Default 32px is bigger
 * than the app's small 22px steppers (sets count, cart quantity) since
 * those are low-stakes increment taps repeated in place, while these are
 * one-shot destructive/navigational actions that deserve a clearer target.
 * `background` defaults to transparent (every existing call site sits
 * cleanly on its own panel background already); pass a solid color for a
 * button positioned overlapping something else (e.g. a bubble's border),
 * so the fill masks whatever's behind it instead of showing through.
 */
const IconButton = ({ icon, onClick, disabled, label, color = "#232526", size = 32, background = "none", ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    {...rest}
    style={{
      width: `${size}px`,
      height: `${size}px`,
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background,
      border: `2px solid ${color}`,
      borderRadius: "50%",
      color,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.5 : 1,
      padding: 0
    }}
  >
    <svg
      width={size * 0.5}
      height={size * 0.5}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[icon]}
    </svg>
  </button>
);

export default IconButton;
